import { 
  collection, 
  addDoc, 
  setDoc,
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc,
  query, 
  orderBy, 
  where,
  Timestamp,
  limit as firestoreLimit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { MinecraftResource, MinecraftResourceUploadData, MinecraftResourceType } from '../types/minecraft';

// 타입 재export
export type { MinecraftResourceType } from '../types/minecraft';

const MINECRAFT_COLLECTION = 'minecraft_resources';

// 리소스 업로드
export const uploadMinecraftResource = async (
  data: MinecraftResourceUploadData,
  resourceId?: string
): Promise<string> => {
  const id = resourceId || `mc_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  const docRef = doc(db, MINECRAFT_COLLECTION, id);
  await setDoc(docRef, {
    ...data,
    downloadCount: 0,
    likes: 0,
    likedBy: [],
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return id;
};

// 파일 업로드
export const uploadMinecraftFile = async (file: File, resourceId: string): Promise<string> => {
  const storageRef = ref(storage, `minecraft/${resourceId}/${file.name}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// 썸네일 업로드
export const uploadThumbnail = async (file: File, resourceId: string): Promise<string> => {
  const storageRef = ref(storage, `minecraft/${resourceId}/thumbnail_${Date.now()}.jpg`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// 갤러리 이미지 업로드
export const uploadGalleryImage = async (file: File, resourceId: string, index: number): Promise<string> => {
  const fileExtension = file.name.split('.').pop();
  const storageRef = ref(storage, `minecraft/${resourceId}/gallery_${index}_${Date.now()}.${fileExtension}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// 모든 리소스 조회
export const getMinecraftResources = async (
  type?: MinecraftResourceType,
  limit?: number
): Promise<MinecraftResource[]> => {
  let q = query(collection(db, MINECRAFT_COLLECTION), orderBy('createdAt', 'desc'));
  
  if (type) {
    q = query(q, where('type', '==', type));
  }
  
  if (limit) {
    q = query(q, firestoreLimit(limit));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt.toDate(),
    updatedAt: doc.data().updatedAt.toDate()
  })) as MinecraftResource[];
};

// 특정 리소스 조회
export const getMinecraftResource = async (id: string): Promise<MinecraftResource | null> => {
  const docRef = doc(db, MINECRAFT_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate()
  } as MinecraftResource;
};

// 사용자가 업로드한 리소스 조회
export const getMinecraftResourcesByAuthor = async (authorId: string): Promise<MinecraftResource[]> => {
  const q = query(
    collection(db, MINECRAFT_COLLECTION),
    where('authorId', '==', authorId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt.toDate(),
    updatedAt: doc.data().updatedAt.toDate()
  })) as MinecraftResource[];
};

// 좋아요 토글
export const toggleLike = async (
  resourceId: string, 
  userId: string, 
  currentLikes: number, 
  likedBy: string[]
): Promise<void> => {
  const resourceRef = doc(db, MINECRAFT_COLLECTION, resourceId);
  const isLiked = likedBy.includes(userId);
  
  const newLikedBy = isLiked 
    ? likedBy.filter(id => id !== userId)
    : [...likedBy, userId];
    
  await updateDoc(resourceRef, {
    likes: isLiked ? currentLikes - 1 : currentLikes + 1,
    likedBy: newLikedBy,
    updatedAt: Timestamp.now()
  });
};

// 다운로드 수 증가
export const incrementDownloadCount = async (resourceId: string, currentCount: number): Promise<void> => {
  const resourceRef = doc(db, MINECRAFT_COLLECTION, resourceId);
  await updateDoc(resourceRef, {
    downloadCount: currentCount + 1,
    updatedAt: Timestamp.now()
  });
};

// 리소스 업데이트
export const updateMinecraftResource = async (
  resourceId: string,
  data: Partial<MinecraftResourceUploadData>,
  existingResource: MinecraftResource
): Promise<void> => {
  const docRef = doc(db, MINECRAFT_COLLECTION, resourceId);
  
  // 업데이트할 데이터 준비 (undefined 제거)
  const updateData: any = {
    updatedAt: Timestamp.now()
  };
  
  // 업데이트된 필드만 추가
  if (data.title !== undefined) updateData.title = data.title;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.type !== undefined) updateData.type = data.type;
  if (data.tags !== undefined) updateData.tags = data.tags;
  if (data.fileUrl !== undefined) updateData.fileUrl = data.fileUrl;
  if (data.fileName !== undefined) updateData.fileName = data.fileName;
  if (data.fileSize !== undefined) updateData.fileSize = data.fileSize;
  if (data.thumbnailUrl !== undefined) updateData.thumbnailUrl = data.thumbnailUrl;
  if (data.version !== undefined) updateData.version = data.version;
  if (data.minecraftVersion !== undefined) updateData.minecraftVersion = data.minecraftVersion;
  
  // 월드 상세 정보
  if (data.subtitle !== undefined) updateData.subtitle = data.subtitle;
  if (data.overview !== undefined) updateData.overview = data.overview;
  if (data.learningObjectives !== undefined) updateData.learningObjectives = data.learningObjectives;
  if (data.recommendedGrade !== undefined) updateData.recommendedGrade = data.recommendedGrade;
  if (data.playTime !== undefined) updateData.playTime = data.playTime;
  if (data.playerMode !== undefined) updateData.playerMode = data.playerMode;
  if (data.prerequisites !== undefined) updateData.prerequisites = data.prerequisites;
  if (data.installationSteps !== undefined) updateData.installationSteps = data.installationSteps;
  if (data.tips !== undefined) updateData.tips = data.tips;
  if (data.teacherGuideUrl !== undefined) updateData.teacherGuideUrl = data.teacherGuideUrl;
  if (data.gallery !== undefined) updateData.gallery = data.gallery;
  if (data.lessonIdeas !== undefined) updateData.lessonIdeas = data.lessonIdeas;
  if (data.missions !== undefined) updateData.missions = data.missions;
  if (data.resourceLinks !== undefined) updateData.resourceLinks = data.resourceLinks;
  
  await updateDoc(docRef, updateData);
};

// 리소스 삭제
export const deleteMinecraftResource = async (resourceId: string, fileUrl: string, thumbnailUrl?: string): Promise<void> => {
  // Firestore 문서 삭제
  const resourceRef = doc(db, MINECRAFT_COLLECTION, resourceId);
  await deleteDoc(resourceRef);
  
  // Storage 파일 삭제
  try {
    // 파일 URL에서 경로 추출
    // URL 형식: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)/);
    if (pathMatch) {
      const filePath = decodeURIComponent(pathMatch[1]);
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    }
    
    // 썸네일 삭제
    if (thumbnailUrl) {
      const thumbUrl = new URL(thumbnailUrl);
      const thumbPathMatch = thumbUrl.pathname.match(/\/o\/(.+)/);
      if (thumbPathMatch) {
        const thumbPath = decodeURIComponent(thumbPathMatch[1]);
        const thumbnailRef = ref(storage, thumbPath);
        await deleteObject(thumbnailRef);
      }
    }
  } catch (error) {
    console.error('파일 삭제 중 오류:', error);
    // 파일 삭제 실패해도 문서는 삭제됨
  }
};

// 검색 (제목, 설명, 태그)
export const searchMinecraftResources = async (
  searchTerm: string,
  type?: MinecraftResourceType
): Promise<MinecraftResource[]> => {
  const allResources = await getMinecraftResources(type);
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return allResources.filter(resource => 
    resource.title.toLowerCase().includes(lowerSearchTerm) ||
    resource.description.toLowerCase().includes(lowerSearchTerm) ||
    resource.tags.some(tag => tag.toLowerCase().includes(lowerSearchTerm))
  );
};

